import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import QaTestedBadge from "../QaTestedBadge/QaTestedBadge";

const ROWS_PER_PAGE = 8;

const matchesRecipientSearch = (recipient, query) => {
  const name = (recipient.recipient_name || "").toLowerCase();
  const email = (recipient.recipient_email || "").toLowerCase();
  const roll = (recipient.roll_number || "").toString().toLowerCase();

  return name.includes(query) || email.includes(query) || roll.includes(query);
};

export default function VerifyCredentialsModal({
  isOpen,
  onClose,
  onConfirm,
  recipients = [],
  isLoading = false,
  testId = "verify-credentials-modal",
}) {
  const { t } = useTranslation();
  const recipientsRef = useRef(recipients);

  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  recipientsRef.current = recipients;

  useEffect(() => {
    if (!isOpen) return;

    setSelectedIds(
      recipientsRef.current.map((recipient) => recipient.credential_id),
    );
    setSearchQuery("");
    setCurrentPage(0);
  }, [isOpen]);

  const filteredRecipients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return recipients;

    return recipients.filter((recipient) =>
      matchesRecipientSearch(recipient, query),
    );
  }, [recipients, searchQuery]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRecipients.length / ROWS_PER_PAGE),
  );

  const safePage = Math.min(currentPage, totalPages - 1);

  useEffect(() => {
    if (currentPage > totalPages - 1) {
      setCurrentPage(totalPages - 1);
    }
  }, [currentPage, totalPages]);

  const paginatedRecipients = useMemo(() => {
    const start = safePage * ROWS_PER_PAGE;
    return filteredRecipients.slice(start, start + ROWS_PER_PAGE);
  }, [filteredRecipients, safePage]);

  const filteredIds = useMemo(
    () => filteredRecipients.map((recipient) => recipient.credential_id),
    [filteredRecipients],
  );

  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedIds.includes(id));

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    onClose();
  };

  const toggleRecipient = (credentialId, checked) => {
    setSelectedIds((prev) =>
      checked
        ? [...new Set([...prev, credentialId])]
        : prev.filter((id) => id !== credentialId),
    );
  };

  const handleSelectAllFiltered = (checked) => {
    if (checked) {
      setSelectedIds((prev) => [...new Set([...prev, ...filteredIds])]);
      return;
    }

    setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
  };

  const handleConfirm = () => {
    onConfirm(selectedIds);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            id={`${testId}-backdrop`}
            className="fixed inset-0 bg-black/25 backdrop-blur-md"
            onClick={handleClose}
            aria-hidden="true"
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id={testId}
                className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all relative"
              >
                <button
                  type="button"
                  id={`${testId}-close`}
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={handleClose}
                >
                  <span className="sr-only">Close</span>
                  &#10005;
                </button>

                <Dialog.Title
                  as="h3"
                  className="text-lg font-semibold leading-6 text-gray-900"
                >
                  {t("workspace:verify_certificates_title", {
                    count: selectedIds.length,
                  })}
                </Dialog.Title>

                <p className="mt-2 text-sm text-gray-500">
                  {t("workspace:verify_certificates_description")}
                </p>

                <div className="flex flex-col md:flex-row items-center justify-between gap-3 mt-4">
                  <div className="relative w-full md:w-[22rem]">
                    <input
                      type="text"
                      id={`${testId}-search`}
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(0);
                      }}
                      placeholder={t("workspace:verify_search_placeholder")}
                      className="w-full rounded-lg border border-gray-300 bg-cfSecondary py-2 px-3 text-sm outline-none focus:border-cfPrimaryButton focus:ring-1 focus:ring-cfPrimaryButton"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      id={`${testId}-cancel`}
                      onClick={handleClose}
                      disabled={isLoading}
                      className="py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 focus:z-10 focus:ring-3 focus:ring-gray-100 disabled:opacity-50"
                    >
                      {t("workspace:cancel")}
                    </button>
                    <button
                      type="button"
                      id={`${testId}-confirm`}
                      onClick={handleConfirm}
                      disabled={isLoading || selectedIds.length === 0}
                      className="text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center bg-cfPrimaryButton hover:bg-cfPrimaryDark disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading
                        ? t("workspace:verifying")
                        : t("workspace:verify")}
                    </button>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-gray-300 overflow-auto">
                  <table className="w-full min-w-[50rem]">
                    <thead className="bg-cfTableHeaderBackgroundColor">
                      <tr>
                        <th className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            id={`${testId}-select-all`}
                            checked={allFilteredSelected}
                            onChange={(e) =>
                              handleSelectAllFiltered(e.target.checked)
                            }
                            disabled={filteredIds.length === 0}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                          {t("workspace:name")}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                          {t("workspace:email")}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                          {t("workspace:roll_number")}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center whitespace-nowrap">
                          {t("workspace:notified_at")}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                          {t("workspace:verification")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedRecipients.length > 0 ? (
                        paginatedRecipients.map((recipient) => (
                          <tr
                            key={recipient.credential_id}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(
                                  recipient.credential_id,
                                )}
                                onChange={(e) =>
                                  toggleRecipient(
                                    recipient.credential_id,
                                    e.target.checked,
                                  )
                                }
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-center">
                              {recipient.recipient_name ||
                                t("workspace:not_applicable")}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-center">
                              {recipient.recipient_email ||
                                t("workspace:not_applicable")}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-center">
                              {recipient.roll_number ||
                                t("workspace:not_applicable")}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap text-center">
                              {recipient.notified_at ||
                                t("workspace:not_applicable")}
                            </td>
                            <td className="px-4 py-3 text-sm text-center">
                              <QaTestedBadge qaTested={recipient.qa_status} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-6 text-sm font-medium text-gray-500 text-center"
                          >
                            {t("workspace:no_result_found")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(0, prev - 1))
                    }
                    disabled={safePage === 0}
                    className={`flex items-center w-[7rem] justify-center gap-2 rounded-md border bg-cfSecondary px-4 py-2 text-sm capitalize transition-colors duration-200 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                        />
                      </svg>
                      <span>{t("workspace:previous")}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(totalPages - 1, prev + 1),
                      )
                    }
                    disabled={safePage >= totalPages - 1}
                    className={`flex items-center w-[7rem] justify-center gap-2 rounded-md border bg-cfSecondary px-4 py-2 text-sm capitalize transition-colors duration-200 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span>{t("workspace:next")}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
